/** @jsx jsx */

/** @jsxFrag React.Fragment */

/** eslint-disable react/sort-comp */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useContext, useEffect, useState } from 'react';

import { groupBy, orderBy } from 'lodash';

import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { InlineNotification, breakpoints } from '@coursera/cds-core';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { VideoContext } from 'bundles/clips/components/ClipsVideoRootContainer';
import VideoEndOverlay from 'bundles/clips/components/VideoEndOverlay';
import type { ProgramSuggestion } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import { sendEvent } from 'bundles/clips/components/short-form-content/VideoProgressEventAPIUtils';
import { hasVideosFiltered } from 'bundles/clips/components/short-form-content/utils';
import type { Partner, Rating, VLPItem } from 'bundles/clips/types/vlpSharedTypes';
import extractPreviewImagesFromOnDemandVideos from 'bundles/clips/utils/extractPreviewImagesFromOnDemandVideos';
import extractSourcesFromOnDemandVideos from 'bundles/clips/utils/extractSourcesFromOnDemandVideos';
import useTrackVideoProgressMutation from 'bundles/clips/utils/useTrackVideoProgressMutation';
import getVideoQuizData from 'bundles/clips/utils/videoQuizData';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { ChangedResponseContextProvider } from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponseContext';
import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import VideoPlayer from 'bundles/general-video-player/components/VideoPlayer';
import type {
  Cuepoint,
  TimeUpdateEventHandler,
  VideoPlayerRef,
} from 'bundles/general-video-player/components/VideoPlayer';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseMaterialItemsV2 from 'bundles/naptimejs/resources/onDemandCourseMaterialItems.v2';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandVideosV1 from 'bundles/naptimejs/resources/onDemandVideos.v1';
import { userPreferences } from 'bundles/video-player';
import { VideoQuiz } from 'bundles/video-quiz/index';
import type { InVideoQuestion } from 'bundles/video-quiz/types';
import { getSyncPlayerOnTimeUpdate, syncPlayerDuration, useVideoTrimmingContext } from 'bundles/video-trim';

import _t from 'i18n!nls/clips';

type Props = {
  courseId?: string;
  courseName?: string;
  courseSlug: string;
  lectureItems: Array<VLPItem>;
  onDemandVideos: OnDemandVideosV1;
  videoItem: OnDemandCourseMaterialItemsV2;
  tracks: TrackList;
  ratings?: Rating | null;
  skills: Array<string>;
  partners?: Array<Partner> | null;
  justEnrolledThroughMutation?: boolean;
  accessProvidedViaProgram?: ProgramSuggestion;
  organizationId?: string | null;
  programId?: string | null;
  startAt?: number;
  onEnroll: () => Promise<any>;
  isEnrolling?: boolean;
};

const VIDEO_PROGRESS_TRACKING_INTERVAL_MS = 30000;

// NOTE: this error message occurs when trackVideoProgressMutation is called but the user is not logged in.
// see usage site below for more context
const MISSING_USER_ERROR_MSG =
  'Please, ensure that user is logged in, courseId and itemId are provided, video length and video progress are positive and video progress is not larger than video length. UserId null |';

const styles = {
  videoWrapper: css`
    ${breakpoints.down('xs')} {
      margin: -16px -16px -10px -16px;
    }
  `,
};

const ClipsVideoPlayer = forwardRef<VideoPlayerRef, Props>(function ClipsVideoPlayer(
  {
    courseId,
    courseName,
    courseSlug,
    lectureItems,
    onDemandVideos,
    videoItem,
    tracks,
    ratings,
    skills,
    partners,
    accessProvidedViaProgram,
    organizationId,
    programId,
    justEnrolledThroughMutation,
    startAt,
    onEnroll,
    isEnrolling,
  },
  forwardedRef
) {
  const router = useRouter();
  const ref = React.useRef<VideoPlayerRef>(null);
  const isPlayNow = router.location.query.playNow === 'true';

  const { trimmedDurationSeconds, trimmedStartTimeSeconds, trimmedEndTimeSeconds } = useVideoTrimmingContext();
  const trackComponent = useRetracked();
  const [isEndOfVideo, setIsEndOfVideo] = useState(false);

  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(() => userPreferences.get('autoplay'));

  const [isVideoCompletedTracked, setIsVideoCompletedTracked] = useState(false);

  const { setIsPlaying, duration, setDuration } = useContext(VideoContext);

  const [lastTrackedProgress, setLastTrackedProgress] = useState(0);

  const [trackVideoProgressMutation] = useTrackVideoProgressMutation();

  const [isInVideoQuizDialogOpen, setIsInVideoQuizDialogOpen] = useState(false);
  const [currentVideoQuizQuestionIndex, setCurrentVideoQuizQuestionIndex] = useState<number | null>(null);
  const [videoQuizQuestions, setVideoQuizQuestions] = useState<Array<InVideoQuestion<QuizQuestionPrompt>>>([]);
  const [videoQuizSessionId, setVideoQuizSessionId] = useState<string | null>(null);

  const trackVideoProgress = (time: number) => {
    if (user.isAuthenticatedUser() && courseId && videoItem.id && trackVideoProgressMutation && duration) {
      setLastTrackedProgress(time);
      trackVideoProgressMutation({
        variables: {
          courseId,
          itemId: videoItem.id,
          totalVideoDurationMs: Math.ceil(1000 * duration),
          currentVideoProgressMs: Math.ceil(1000 * time),
          programId,
          organizationId,
        },
      }).catch((e: unknown) => {
        // in the (rare) event that the user has logged out on a different tab,
        // we look for MISSING_USER_ERROR_MSG and if so, reload the page (which will prompt for a re-login)
        // while rare, the error pops up periodically as a (spurious) sentry issue
        if ((typeof e === 'string' || typeof e === 'object') && e?.toString().includes(MISSING_USER_ERROR_MSG)) {
          window.location.reload();
        }
      });
    }
  };

  const handleInVideoQuiz = (questions: Array<InVideoQuestion<QuizQuestionPrompt>>) => () => {
    setVideoQuizQuestions(questions);
    setCurrentVideoQuizQuestionIndex(0);
    setIsInVideoQuizDialogOpen(true);
    ref.current?.pause();
  };

  const handleIVQNextQuestionOrContinue = () => {
    // do nothing if in wrong state
    if (currentVideoQuizQuestionIndex === null) {
      return;
    }

    if (currentVideoQuizQuestionIndex < videoQuizQuestions.length - 1) {
      // has next qusetion
      setCurrentVideoQuizQuestionIndex(currentVideoQuizQuestionIndex + 1);
    } else {
      // no more question, close dialog
      setCurrentVideoQuizQuestionIndex(null);
      setVideoQuizQuestions([]);
      setIsInVideoQuizDialogOpen(false);
      ref.current?.play();
    }
  };

  // get IVQ questions
  useEffect(() => {
    getVideoQuizData(courseSlug, videoItem.id, user.get().id).then((videoQuizData) => {
      if (videoQuizData && ref.current) {
        const groupedQuestions = groupBy(orderBy(videoQuizData.questions, 'video.order', 'desc'), 'video.cuePointMs');
        const cuepoints = Object.keys(groupedQuestions).map((cuePointMs) => {
          const questions = groupedQuestions[cuePointMs];
          return {
            at: parseInt(cuePointMs, 10) / 1000,
            to: parseInt(cuePointMs, 10) / 1000 + 0.5,
            callback: handleInVideoQuiz(questions),
            marker: true,
          };
        });

        ref.current.addCuepoints(cuepoints);

        setVideoQuizSessionId(videoQuizData.sessionId);
      }
    });
  }, [courseSlug, videoItem.id]);

  const handleBeforePlay = () => {
    trackComponent({ trackingData: {}, trackingName: 'short_form_content_click_play_video_button', action: 'click' });

    return new Promise((resolve: (value: boolean) => void) => {
      const haveToEnrollToPlay = hasVideosFiltered(onDemandVideos) && !isEnrolling;

      if (haveToEnrollToPlay) {
        onEnroll().then(() => {
          trackComponent({ trackingData: {}, trackingName: 'sfc_video', action: 'enrolled_to_play' });
          resolve(true);
        });
      } else {
        resolve(true);
      }
    });
  };

  const handleVideoCompleteCuepoint = () => {
    if (!isVideoCompletedTracked) {
      trackComponent({ trackingData: {}, trackingName: 'short_form_content_video_completed', action: 'watch' });
      sendEvent('ended', videoItem.id, courseSlug).then(() => {
        setIsVideoCompletedTracked(true);
      });
    }
  };

  const handleOnMetadataLoaded = () => {
    const syncPlayer = getSyncPlayerOnTimeUpdate({
      player: ref.current,
      trimmedDurationSeconds,
      trimmedStartTimeSeconds,
    });
    // we need the video player to send the 'ended' event relative to the original start time
    syncPlayerDuration(ref.current, trimmedEndTimeSeconds);
    syncPlayer();
  };

  // durationchange means the video is loaded
  const handleDurationChange = (d: number) => {
    ref.current?.addCuepoints([{ at: '90%', to: '100%', callback: handleVideoCompleteCuepoint }]);
    setDuration(d);
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);

    trackComponent({ trackingData: {}, trackingName: 'sfc_video_started', action: 'watch' });
    trackVideoProgress(0);
    setIsEndOfVideo(false);
    sendEvent('play', videoItem.id, courseSlug);

    // remove playNow query param from url
    if (isPlayNow) {
      const {
        location: { query, pathname },
      } = router;
      router.replace({ pathname, query: { ...query, playNow: undefined } });
    }
  };

  const handleVideoEnded = () => {
    trackComponent({ trackingData: {}, trackingName: 'sfc_video', action: 'ended' });
    trackVideoProgress(duration);
    setIsEndOfVideo(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
    trackComponent({ trackingData: {}, trackingName: 'sfc_video', action: 'paused' });
  };

  const handleVideoReady = () => {
    setIsVideoReady(true);
  };

  const handleTimeUpdate = (t: number) => {
    const syncPlayer = getSyncPlayerOnTimeUpdate({
      player: ref.current,
      trimmedDurationSeconds,
      trimmedStartTimeSeconds,
    });

    if (duration > 0 && duration - t < 1) {
      setIsEndOfVideo(true);
    } else {
      setIsEndOfVideo(false);
    }

    if ((t - lastTrackedProgress) * 1000 > VIDEO_PROGRESS_TRACKING_INTERVAL_MS) {
      trackVideoProgress(t);
    }

    syncPlayer();
  };

  const handleAutoPlayChange = (autoplay: boolean) => {
    setIsAutoPlayEnabled(autoplay);
    userPreferences.set('autoplay', autoplay);
  };

  React.useImperativeHandle(forwardedRef, () => ({
    play: () => ref.current?.play(),
    pause: () => ref.current?.pause(),
    seekTo: (t: number) => ref.current?.seekTo(t),
    reloadSources: () => ref.current?.reloadSources(),
    addCuepoints: (cuepoints: Array<Cuepoint>) => ref.current?.addCuepoints(cuepoints),
    addTimeUpdateEventHandler: (handler: TimeUpdateEventHandler) => ref.current?.addTimeUpdateEventHandler(handler),
    removeTimeUpdateEventHandler: (handler?: TimeUpdateEventHandler) =>
      ref.current?.removeTimeUpdateEventHandler(handler),
    duration: (t?: number) => ref.current?.duration(t),
    currentTime: (t?: number) => ref.current?.currentTime(t),
    trigger: (event: string) => ref.current?.trigger(event),
  }));

  const partner = partners && partners[0];
  const sourcesByResolution = extractSourcesFromOnDemandVideos(onDemandVideos);

  const previewImagesByResolution = extractPreviewImagesFromOnDemandVideos(onDemandVideos);

  const textTracks = tracks
    ?.toArray()
    // @ts-expect-error TODO: fix noImplicitAny error here
    .map(({ label, languageCode, url }) => ({ label, language: languageCode, src: url }));

  const isOverlayEnabled = isEnrolling || isEndOfVideo || isInVideoQuizDialogOpen;

  return (
    <ErrorBoundaryWithTagging
      componentName="ClipsVideoPlayer"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error with the video player. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <ChangedResponseContextProvider>
        <div css={styles.videoWrapper} data-testid="clips-video-player">
          <VideoPlayer
            ref={ref}
            sentryErrorSource="ClipsVideoPlayer"
            sourcesByResolution={sourcesByResolution}
            previewImagesByResolution={previewImagesByResolution}
            onBeforePlay={handleBeforePlay}
            onPlay={handleVideoPlay}
            onEnded={handleVideoEnded}
            onPause={handleVideoPause}
            onTimeUpdate={handleTimeUpdate}
            onDurationChange={handleDurationChange}
            onReady={handleVideoReady}
            onLoadedMetadata={handleOnMetadataLoaded}
            textTracks={textTracks}
            // TODO: currently autoplay is used as a workaround to handle the "justEnrolledThroughMutation" state
            // we should support real auto play once loading skeleton is done
            isAutoPlayEnabled={isAutoPlayEnabled}
            isInstantPlay={justEnrolledThroughMutation || isPlayNow}
            onAutoPlayChange={handleAutoPlayChange}
            isOverlayEnabled={isOverlayEnabled}
            startAt={startAt}
          >
            {(!isVideoReady || isEnrolling) && <SvgLoaderSignal size={64} />}
            {isInVideoQuizDialogOpen && currentVideoQuizQuestionIndex !== null && videoQuizSessionId && (
              <VideoQuiz
                key={videoQuizQuestions[currentVideoQuizQuestionIndex].id}
                onContinue={handleIVQNextQuestionOrContinue}
                itemId={videoItem.id}
                courseSlug={courseSlug}
                question={videoQuizQuestions[currentVideoQuizQuestionIndex]}
                sessionId={videoQuizSessionId}
              />
            )}
            {isEndOfVideo && (
              <VideoEndOverlay
                courseId={courseId}
                courseName={courseName}
                courseSlug={courseSlug}
                lectureItems={lectureItems}
                itemId={videoItem.id}
                partner={partner}
                ratings={ratings}
                skills={skills}
                isAutoPlay={isAutoPlayEnabled}
                accessProvidedViaProgram={accessProvidedViaProgram}
              />
            )}
          </VideoPlayer>
        </div>
      </ChangedResponseContextProvider>
    </ErrorBoundaryWithTagging>
  );
});

const ErrorBoundClipsVideoPlayer = forwardRef<VideoPlayerRef, Props>((props: Props, ref) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="ClipsVideoPlayer"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t(
            'An error was encountered while playing this video, and our development team has been alerted. Please try again later.'
          )}
        </InlineNotification>
      }
    >
      <ClipsVideoPlayer {...props} ref={ref} />
    </ErrorBoundaryWithTagging>
  );
});

export default ErrorBoundClipsVideoPlayer;
